import config from '../aws-config';

// Marketing API client for the Operations dashboard.
// In production the browser calls the marketing API directly (REACT_APP_MARKETING_API_URL);
// its CORS allowlist includes admin.homecareofmi.com. Under `npm start` requests go to the
// CRA dev proxy at /marketing-api (see src/setupProxy.js), which forwards them for localhost.
// The token getter is async because AuthContext.getToken is async.
const BASE = process.env.NODE_ENV === 'development'
  ? '/marketing-api'
  : (process.env.REACT_APP_MARKETING_API_URL || '').replace(/\/$/, '');

// Default token getter: the signed-in Cognito session, the same source utils/api.js uses.
// Pages outside /marketing (the Operations dashboard cards, the sidebar badges) call this
// API before MarketingLayout has mounted, so the default must not be "no token".
async function sessionToken() {
  return new Promise((resolve) => {
    const { CognitoUserPool } = require('amazon-cognito-identity-js');
    const pool = new CognitoUserPool({
      UserPoolId: config.userPoolId,
      ClientId: config.userPoolClientId,
    });
    const user = pool.getCurrentUser();
    if (!user) return resolve(null);
    user.getSession((err, session) => {
      if (err || !session?.isValid()) return resolve(null);
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

let _getToken = sessionToken;
export const setTokenGetter = (fn) => { _getToken = fn || sessionToken; };

async function req(method, path, body) {
  let token = null;
  try { token = await _getToken(); } catch (e) { token = null; }
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export const marketingApi = {
  getDashboard: () => req('GET', '/dashboard'),

  getPosts: (qs = '') => req('GET', `/posts${qs}`),
  triggerPost: () => req('POST', '/posts/trigger'),
  triggerReel: () => req('POST', '/posts/trigger-reel'),

  getBrandVoice: () => req('GET', '/brand-voice'),
  triggerAnalysis: () => req('POST', '/brand-voice/analyze'),
  getUploadUrl: () => req('GET', '/brand-voice/upload-url'),
  savePosts: (body) => req('POST', '/brand-voice/posts', body),
  getStoredPosts: () => req('GET', '/brand-voice/posts'),
  deleteStoredPosts: () => req('DELETE', '/brand-voice/posts'),

  getEscalations: (qs = '') => req('GET', `/escalations${qs}`),
  resolveEscalation: (id) => req('PUT', `/escalations/${id}/resolve`),

  getLeads: (qs = '') => req('GET', `/leads${qs}`),
  updateLead: (id, status) => req('PUT', `/leads/${id}/status`, { status }),

  getComments: (qs = '') => req('GET', `/comments${qs}`),

  getMessages: (qs = '') => req('GET', `/messages${qs}`),
  fetchMessages: () => req('POST', '/messages/fetch'),

  getAuditLog: (qs = '') => req('GET', `/audit${qs}`),

  getReelAssets: () => req('GET', '/assets/reel'),
  getReelUploadUrl: (type, filename = '') => req('GET', `/assets/reel/upload-url?type=${type}${filename ? '&filename=' + encodeURIComponent(filename) : ''}`),
  deleteReelAsset: (key) => req('DELETE', `/assets/reel?key=${encodeURIComponent(key)}`),

  getPostSchedule: () => req('GET', '/post-schedule'),
  updatePostSchedule: (schedule) => req('PUT', '/post-schedule', { schedule }),
  setPostScheduleState: (state) => req('PUT', '/post-schedule', { state }),

  getBrandImages: () => req('GET', '/brand-images'),
  getBrandImageUploadUrl: (filename, type) => req('GET', `/brand-images/upload-url?filename=${encodeURIComponent(filename)}&type=${encodeURIComponent(type)}`),
  confirmBrandImageUpload: (body) => req('POST', '/brand-images/confirm', body),
  deleteBrandImage: (key) => req('DELETE', `/brand-images/${encodeURIComponent(key)}`),
};

export const api = marketingApi;
